import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import { useSubscription } from "shared/contexts/Subscription";

import ContentHeaderSection from "shared/common/ContentHeaderSection";
import { useMasterList } from "shared/hooks/useMasterList";
import masterApiCall from "shared/services/MasterDataApi";
import apiCall from "shared/services/ApiAdapter";
import { getUserRoleProfileId } from "shared/utils/SessionDataUtils";

const CreateUpdateCourseForm = ({
  navigateTo,
  courseId,
  useMockData,
  displayToast,
  userId,
}) => {
  const { t } = useTranslation();
  const { limits, canPerformAction } = useSubscription();
  const { startLoading, stopLoading } = useLoading();
  console.log(courseId);
  const {
    masterData,
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useMasterList({
    useMockData,
    displayToast,
    startLoading,
    stopLoading,
    apiCall: masterApiCall,
  });

  // Helper: normalize various inputs (id/object/code) into a code using master list
  const resolveToCode = useCallback((list, valueOrObject) => {
    if (!list || !Array.isArray(list)) return "";
    if (valueOrObject == null) return "";
    // If already a string code
    if (typeof valueOrObject === "string") return valueOrObject;
    // If it's a number, treat as id
    if (typeof valueOrObject === "number") {
      const byId = list.find((it) => it.id === valueOrObject);
      return byId ? byId.value : "";
    }
    // If it's an object with code/value/id
    if (typeof valueOrObject === "object") {
      if (valueOrObject.code) return valueOrObject.code;
      if (valueOrObject.value) return valueOrObject.value;
      if (valueOrObject.id != null) {
        const byId = list.find((it) => it.id === valueOrObject.id);
        return byId ? byId.value : "";
      }
    }
    return "";
  }, []);

  const calculateDurationInMonths = useCallback((startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) {
      return 0;
    }
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const months =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());
    return Math.max(0, months);
  }, []);

  const today = new Date();
  const oneYearFromToday = new Date(today);
  oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

  const defaultStartDate = today.toISOString().split("T")[0];
  const defaultEndDate = oneYearFromToday.toISOString().split("T")[0];
  const defaultDuration = calculateDurationInMonths(
    defaultStartDate,
    defaultEndDate
  );

  const [course, setCourse] = useState({
    courseName: "",
    curriculum: "",
    academicClass: "",
    subject: "",
    description: "",
    durationMonths: defaultDuration,
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    isActive: true,
    baseFee: 0,
    createdBy: "",
    updatedBy: "",
    createdDate: null,
    updatedDate: null,
  });

  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(userId);

  useEffect(() => {
    try {
      const userProfile = sessionStorage.getItem("app_userprofile");
      if (userProfile) {
        const userData = JSON.parse(userProfile);
        if (userData && userData.id) {
          setCurrentUserId(userData.id);
        }
      }
    } catch (error) {
      console.error("Failed to parse user profile from sessionStorage:", error);
    }
  }, []);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (courseId) {
        console.log(
          `[CreateUpdateCourseForm] Fetching course details for ID: ${courseId}`
        );
        setFormLoading(true);
        try {
          const existingCourse = await apiCall(
            `/courses/${courseId}`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );
          console.log(
            `[CreateUpdateCourseForm] Result of apiCall for course details:`,
            existingCourse
          );

          if (existingCourse) {
            // Normalize curriculum/class/subject to codes from masterData
            const normalized = {
              ...existingCourse,
              curriculum: resolveToCode(
                masterData?.curricula,
                existingCourse.curriculum ??
                existingCourse.curriculumId ??
                existingCourse.curriculumCode
              ),
              academicClass: resolveToCode(
                masterData?.academicClasses,
                existingCourse.academicClass ??
                existingCourse.classStandardId ??
                existingCourse.classCode
              ),
              subject: resolveToCode(
                masterData?.subjects,
                existingCourse.subject ??
                existingCourse.subjectId ??
                existingCourse.subjectCode
              ),
            };
            setCourse(normalized);
            console.log(
              `[CreateUpdateCourseForm] Course state updated with fetched data:`,
              existingCourse
            );
          } else {
            displayToast(t("courseNotFound", { courseId }), "error");
            console.error(
              `[CreateUpdateCourseForm] Course with ID ${courseId} not found.`
            );
          }
        } catch (error) {
          console.error(
            "[CreateUpdateCourseForm] Failed to fetch course details:",
            error
          );
          displayToast(
            t("failedToLoadCourseDetails", { message: error.message }),
            "error"
          );
        } finally {
          setFormLoading(false);
        }
      } else {
        console.log(
          "[CreateUpdateCourseForm] Creating new course - form cleared."
        );
        setCourse({
          courseName: "",
          curriculum: "",
          academicClass: "",
          subject: "",
          description: "",
          durationMonths: defaultDuration,
          startDate: defaultStartDate,
          endDate: defaultEndDate,
          isActive: true,
          baseFee: 0,
          createdBy: "",
          updatedBy: "",
          createdDate: null,
          updatedDate: null,
        });
      }
    };

    if (!masterDataLoading) {
      fetchCourseDetails();
    }
  }, [
    courseId,
    useMockData,
    displayToast,
    startLoading,
    stopLoading,
    defaultStartDate,
    defaultEndDate,
    defaultDuration,
    masterDataLoading,
    t,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    setCourse((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!course.courseName) newErrors.courseName = t("courseNameRequired");
    if (!course.curriculum) newErrors.curriculum = t("curriculumRequired");
    if (!course.academicClass)
      newErrors.academicClass = t("academicClassRequired");
    if (!course.subject) newErrors.subject = t("subjectRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      displayToast(t("pleaseCorrectFormErrors"), "error");
      return;
    }

    setFormLoading(true);

    // Ensure codes are sent for curriculum, class, subject
    let courseToSave = {
      courseName: course.courseName,
      curriculum: resolveToCode(masterData?.curricula, course.curriculum),
      academicClass: resolveToCode(
        masterData?.academicClasses,
        course.academicClass
      ),
      subject: resolveToCode(masterData?.subjects, course.subject),
      description: course.description,
      durationMonths: calculateDurationInMonths(
        course.startDate,
        course.endDate
      ),
      startDate: course.startDate
        ? new Date(course.startDate).toISOString()
        : null,
      endDate: course.endDate ? new Date(course.endDate).toISOString() : null,
      isActive: !!course.isActive,
      baseFee: Number(course.baseFee) || 0,
      createdBy: course.createdBy,
      createdDate: course.createdDate,
      tutor: {
        id: getUserRoleProfileId(),
        user: {
          id: currentUserId
        }
      }
    };

    try {
      if (courseId) {
        const payload = {
          ...courseToSave,
          id: courseId,
          updatedBy: currentUserId,
          updatedDate: new Date().toISOString(),
        };
        courseToSave = await apiCall(
          `/courses/${courseId}`,
          useMockData,
          "PUT",
          payload,
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("courseUpdatedSuccessfully"), "success");
      } else {
        const payload = {
          ...courseToSave,
          createdBy: currentUserId,
          updatedBy: currentUserId,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        };
        courseToSave = await apiCall(
          "/courses",
          useMockData,
          "POST",
          payload,
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("courseCreatedSuccessfully"), "success");
      }
      navigateTo("myBatches", courseToSave.courseName, courseToSave.id)
    } catch (error) {
      console.error("Failed to save course:", error);
      displayToast(
        t("failedToSaveCourse", { message: error.message }),
        "error"
      );
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={courseId ? t("updateCourse") : t("createCourse")}
        subtitle={t("createOrUpdateCourse")}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        {masterDataLoading || formLoading ? (
          <div className="text-center text-gray-600 dark:text-gray-400">
            {t("loadingCourseDetails")}
          </div>
        ) : masterDataError ? (
          <div className="text-center text-red-500">{masterDataError}</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="courseName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("courseName")}
              </label>
              <input
                type="text"
                id="courseName"
                name="courseName"
                value={course.courseName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
              {errors.courseName && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.courseName}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="curriculum"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("curriculum")}
              </label>
              <select
                id="curriculum"
                name="curriculum"
                value={course.curriculum}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">{t("select")}</option>
                {masterData.curricula.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.curriculum && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.curriculum}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="academicClass"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("class")}
              </label>
              <select
                id="academicClass"
                name="academicClass"
                value={course.academicClass}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">{t("select")}</option>
                {masterData.academicClasses.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.academicClass && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.academicClass}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("subject")}
              </label>
              <select
                id="subject"
                name="subject"
                value={course.subject}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">{t("select")}</option>
                {masterData.subjects.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.subject && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.subject}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("description")}
              </label>
              <textarea
                id="description"
                name="description"
                value={course.description}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              ></textarea>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigateTo("courses")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={formLoading}
              >
                {formLoading
                  ? courseId
                    ? t("updating")
                    : t("creating")
                  : courseId
                    ? t("update")
                    : t("create")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateUpdateCourseForm;
